(() => {
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopQuery = window.matchMedia('(min-width: 700px)');
  const countdownTimers = new WeakMap();
  const railResizeObservers = new WeakMap();
  const run2SuccessEventsInFlight = new Set();
  const run2SuccessEventsCompleted = new Set();
  const RUN2_ATTRIBUTION_STORAGE_KEY = 'skoto_run2_first_touch_v2';
  const RUN2_SIGNUP_ATTEMPT_KEY = 'skoto_run2_signup_attempt_v2';
  const RUN2_SIGNUP_FIRED_KEY = 'skoto_run2_signup_success_fired_v2';
  const RUN2_SUCCESS_EVENT_NAME = 'skoto:run2_lead';
  const RUN2_APPROVED_SIZES = ['S', 'M', 'L', 'XL', 'XXL'];
  const RUN2_ATTRIBUTION_FIELDS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'meta_campaign_id',
    'meta_adset_id',
    'meta_ad_id',
    'placement',
    'site_source_name'
  ];
  const RUN2_ATTRIBUTION_TAG_PREFIXES = {
    utm_source: 'attr-source',
    utm_medium: 'attr-medium',
    utm_campaign: 'attr-campaign',
    utm_content: 'attr-content',
    utm_term: 'attr-term',
    meta_campaign_id: 'meta-campaign-id',
    meta_adset_id: 'meta-adset-id',
    meta_ad_id: 'meta-ad-id',
    placement: 'meta-placement',
    site_source_name: 'meta-site-source'
  };
  const RUN2_ATTRIBUTION_ALIASES = {
    meta_campaign_id: 'utm_id',
    meta_adset_id: 'adset_id',
    meta_ad_id: 'ad_id'
  };
  let revealObserver;
  let heroObserver;
  let scrollFrame;

  const elementsWithin = (root, selector) => {
    const matchesRoot = root.matches?.(selector) ? [root] : [];
    return [...matchesRoot, ...root.querySelectorAll(selector)];
  };

  const publish = (name, payload = {}) => {
    document.dispatchEvent(new CustomEvent(`skoto:${name}`, { detail: payload }));

    try {
      window.Shopify?.analytics?.publish?.(`skoto_${name}`, payload);
    } catch (error) {
      console.debug('[SKOTO analytics]', error);
    }
  };

  const cleanAttributionValue = (value) => String(value || '').trim().replace(/[\u0000-\u001f\u007f]/g, '').slice(0, 120);

  const attribution = () => {
    const params = new URLSearchParams(window.location.search);
    const current = RUN2_ATTRIBUTION_FIELDS.reduce((result, key) => {
      const value = cleanAttributionValue(params.get(key)) ||
        cleanAttributionValue(params.get(RUN2_ATTRIBUTION_ALIASES[key] || key));
      if (value) result[key] = value;
      return result;
    }, {});

    if (params.has('fbclid')) current.meta_click = '1';

    try {
      const stored = JSON.parse(sessionStorage.getItem(RUN2_ATTRIBUTION_STORAGE_KEY) || 'null');

      if (Object.keys(current).length > 0 && !stored) {
        const firstTouch = {
          ...current,
          first_touch_at: new Date().toISOString(),
          landing_path: window.location.pathname
        };
        sessionStorage.setItem(RUN2_ATTRIBUTION_STORAGE_KEY, JSON.stringify(firstTouch));
        return firstTouch;
      }

      return stored || current;
    } catch (error) {
      return current;
    }
  };

  const createRun2EventId = () => {
    if (window.crypto?.randomUUID) return `run2-${window.crypto.randomUUID()}`;

    const randomPart = Math.random().toString(36).slice(2, 12);
    return `run2-${Date.now().toString(36)}-${randomPart}`;
  };

  const run2TagValue = (value) => String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

  const readRecentRun2Attempt = () => {
    try {
      const attempt = JSON.parse(window.sessionStorage.getItem(RUN2_SIGNUP_ATTEMPT_KEY) || 'null');
      const attemptAge = Date.now() - Number(attempt?.timestamp || 0);
      const hasValidId = typeof attempt?.event_id === 'string' && attempt.event_id.length <= 80 &&
        /^run2-[a-z0-9-]+$/i.test(attempt.event_id);
      const hasQualifiedFields = RUN2_APPROVED_SIZES.includes(attempt?.hoodie_size) && attempt?.consent === true;
      const hasFormContext = typeof attempt?.form_id === 'string' && attempt.form_id.length > 0 &&
        typeof attempt?.source === 'string' && attempt.source.length > 0 &&
        typeof attempt?.page_path === 'string' && attempt.page_path.startsWith('/');

      if (hasValidId && hasQualifiedFields && hasFormContext && attemptAge >= 0 && attemptAge < 15 * 60 * 1000) return attempt;
      window.sessionStorage.removeItem(RUN2_SIGNUP_ATTEMPT_KEY);
    } catch (_error) {
      // A native Shopify submission still works when session storage is unavailable.
    }

    return null;
  };

  const publishRun2SignupSuccess = (attempt) => {
    const eventId = attempt?.event_id || '';
    if (!eventId || run2SuccessEventsInFlight.has(eventId) || run2SuccessEventsCompleted.has(eventId)) return;

    try {
      if (window.sessionStorage.getItem(RUN2_SIGNUP_FIRED_KEY) === eventId) return;
    } catch (_error) {
      // Continue with in-page deduplication if session storage is unavailable.
    }

    const payload = {
      event_id: eventId,
      lead_type: 'run2_waitlist',
      signup_source: cleanAttributionValue(attempt.source || 'storefront_run2'),
      release_state: document.body.dataset.skotoReleaseState || 'unknown',
      page_path: cleanAttributionValue(attempt.page_path || window.location.pathname),
      submitted_at: cleanAttributionValue(attempt.submitted_at || '')
    };
    let publishAttempts = 0;

    run2SuccessEventsInFlight.add(eventId);

    const retryOrFinish = (status) => {
      document.documentElement.setAttribute('data-skoto-run2-event-status', status);
      if (publishAttempts < 20) {
        window.setTimeout(dispatch, Math.min(publishAttempts * 250, 1000));
      } else {
        run2SuccessEventsInFlight.delete(eventId);
      }
    };

    const dispatch = () => {
      publishAttempts += 1;
      const analytics = window.Shopify?.analytics;

      if (!analytics || typeof analytics.publish !== 'function') {
        retryOrFinish('shopify-analytics-unavailable');
        return;
      }

      let result;
      try {
        result = analytics.publish(RUN2_SUCCESS_EVENT_NAME, payload);
      } catch (_error) {
        retryOrFinish('publish-error');
        return;
      }

      Promise.resolve(result)
        .then((accepted) => {
          if (accepted === false) {
            retryOrFinish('publish-rejected');
            return;
          }

          run2SuccessEventsCompleted.add(eventId);
          run2SuccessEventsInFlight.delete(eventId);
          try {
            window.sessionStorage.setItem(RUN2_SIGNUP_FIRED_KEY, eventId);
            window.sessionStorage.removeItem(RUN2_SIGNUP_ATTEMPT_KEY);
          } catch (_error) {
            // Keep successful dispatches deduplicated in-page when storage cannot be written.
          }
          document.documentElement.setAttribute('data-skoto-run2-event-status', 'published');
        })
        .catch(() => {
          retryOrFinish('publish-rejected');
        });
    };

    dispatch();
  };

  const getActiveHeroVideo = (hero) => {
    const viewport = desktopQuery.matches ? 'desktop' : 'mobile';
    return hero.querySelector(`[data-skoto-hero-video="${viewport}"] video`);
  };

  const updateHeroControl = (hero, video) => {
    const control = hero.querySelector('[data-skoto-hero-toggle]');
    if (!control) return;

    control.hidden = !video;
    if (!video) return;

    const paused = video.paused || hero.dataset.skotoVideoPaused === 'true';
    const label = control.querySelector('[data-skoto-hero-toggle-label]');
    control.dataset.state = paused ? 'paused' : 'playing';
    control.setAttribute('aria-label', paused ? 'Play background motion' : 'Pause background motion');
    if (label) label.textContent = paused ? 'PLAY' : 'PAUSE';
  };

  const loadVideoSources = (video) => {
    if (video.dataset.skotoSourcesLoaded === 'true') return;

    video.querySelectorAll('source[data-src]').forEach((source) => {
      source.src = source.dataset.src;
    });
    video.dataset.skotoSourcesLoaded = 'true';
    video.load();
  };

  const loadVideoPoster = (video) => {
    if (!video?.dataset.poster || video.poster) return;
    video.poster = video.dataset.poster;
  };

  const syncHero = async (hero) => {
    const video = getActiveHeroVideo(hero);
    const saveData = Boolean(navigator.connection?.saveData);
    const automaticPlaybackAllowed = !motionQuery.matches && !saveData && (hero.dataset.skotoMobileManual !== 'true' || desktopQuery.matches);
    const userRequestedPlayback = hero.dataset.skotoVideoUserRequested === 'true';
    const canPlay = video && (automaticPlaybackAllowed || userRequestedPlayback) && !document.hidden && hero.dataset.skotoInView !== 'false' && hero.dataset.skotoVideoPaused !== 'true';

    hero.querySelectorAll('video').forEach((candidate) => {
      if (candidate !== video) candidate.pause();
    });

    if (!video) {
      updateHeroControl(hero, video);
      return video;
    }

    if (canPlay) {
      loadVideoPoster(video);
      loadVideoSources(video);
      try {
        await video.play();
      } catch (error) {
        hero.dataset.skotoVideoPaused = 'true';
        hero.dataset.skotoVideoUserRequested = 'false';
      }
    } else {
      video.pause();
    }

    updateHeroControl(hero, video);
    return video;
  };

  const syncAllHeroes = () => {
    document.querySelectorAll('.skoto-campaign-hero, [data-skoto-commerce-hero]').forEach(syncHero);
  };

  const initializeHeroes = (root = document) => {
    elementsWithin(root, '.skoto-campaign-hero, [data-skoto-commerce-hero]').forEach((hero) => {
      if (hero.dataset.skotoHeroReady === 'true') {
        syncHero(hero);
        return;
      }

      hero.dataset.skotoHeroReady = 'true';
      hero.dataset.skotoInView = 'true';
      const control = hero.querySelector('[data-skoto-hero-toggle]');

      control?.addEventListener('click', async () => {
        const video = getActiveHeroVideo(hero);
        if (!video) return;

        const shouldPlay = video.paused;
        hero.dataset.skotoVideoPaused = shouldPlay ? 'false' : 'true';
        hero.dataset.skotoVideoUserRequested = shouldPlay ? 'true' : 'false';
        const activeVideo = await syncHero(hero);
        publish('hero_video_toggle', { state: activeVideo && !activeVideo.paused ? 'playing' : 'paused' });
      });

      if (hero.hasAttribute('data-skoto-commerce-hero')) {
        hero.querySelectorAll('video').forEach((video) => {
          video.addEventListener('error', () => {
            hero.dataset.skotoVideoPaused = 'true';
            hero.dataset.skotoVideoUserRequested = 'false';
            updateHeroControl(hero, getActiveHeroVideo(hero));
          });
        });
      }

      heroObserver?.observe(hero);
      syncHero(hero);
    });
  };

  const initializeCountdowns = (root = document) => {
    elementsWithin(root, '[data-skoto-countdown]').forEach((countdown) => {
      if (countdown.dataset.skotoCountdownReady === 'true') return;
      countdown.dataset.skotoCountdownReady = 'true';

      const target = new Date(countdown.dataset.skotoCountdownTarget || '').getTime();
      const output = countdown.querySelector('[data-skoto-countdown-output]');

      if (!output || Number.isNaN(target)) {
        countdown.hidden = true;
        return;
      }

      const update = () => {
        const remaining = target - Date.now();

        if (remaining <= 0) {
          output.textContent = 'COUNTDOWN COMPLETE';
          const timer = countdownTimers.get(countdown);
          if (timer) window.clearInterval(timer);
          countdownTimers.delete(countdown);
          return;
        }

        const totalSeconds = Math.floor(remaining / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        output.textContent = `${days}D ${hours.toString().padStart(2, '0')}H ${minutes.toString().padStart(2, '0')}M ${seconds.toString().padStart(2, '0')}S`;
      };

      update();
      countdownTimers.set(countdown, window.setInterval(update, 1000));
    });
  };

  const initializeWallpaperGalleries = (root = document) => {
    elementsWithin(root, '[data-skoto-wallpaper-gallery]').forEach((gallery) => {
      if (gallery.dataset.skotoWallpaperReady === 'true') return;
      gallery.dataset.skotoWallpaperReady = 'true';

      const filters = [...gallery.querySelectorAll('[data-skoto-wallpaper-filter]')];
      const cards = [...gallery.querySelectorAll('[data-skoto-wallpaper-card]')];
      const count = gallery.querySelector('[data-skoto-wallpaper-visible-count]');

      const applyFilter = (filter) => {
        let visible = 0;

        cards.forEach((card) => {
          const matches = filter === 'all' || card.dataset.wallpaperDevice === filter;
          card.hidden = !matches;
          if (matches) visible += 1;
        });

        filters.forEach((button) => {
          const selected = button.dataset.skotoWallpaperFilter === filter;
          button.classList.toggle('is-active', selected);
          button.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        if (count) count.textContent = String(visible).padStart(2, '0');
      };

      filters.forEach((button) => {
        button.addEventListener('click', () => {
          const filter = button.dataset.skotoWallpaperFilter || 'all';
          applyFilter(filter);
          publish('wallpaper_filter', { filter, visible: cards.filter((card) => !card.hidden).length });
        });
      });
    });
  };

  const initializeRails = (root = document) => {
    elementsWithin(root, '[data-skoto-rail]').forEach((rail) => {
      if (rail.dataset.skotoRailReady === 'true') return;
      rail.dataset.skotoRailReady = 'true';

      const section = rail.closest('[data-skoto-section]') || rail.parentElement;
      const previous = section?.querySelector('[data-skoto-rail-prev]');
      const next = section?.querySelector('[data-skoto-rail-next]');

      const update = () => {
        const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth - 2);
        if (previous) previous.disabled = rail.scrollLeft <= 2;
        if (next) next.disabled = rail.scrollLeft >= maxScroll;
      };

      const move = (direction) => {
        const card = rail.querySelector(':scope > *');
        const distance = card ? card.getBoundingClientRect().width + 12 : rail.clientWidth * 0.84;
        rail.scrollBy({ left: direction * distance, behavior: motionQuery.matches ? 'auto' : 'smooth' });
      };

      previous?.addEventListener('click', () => move(-1));
      next?.addEventListener('click', () => move(1));
      rail.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(rail);
        railResizeObservers.set(rail, resizeObserver);
      }
      update();
    });
  };

  const initializeSignupForms = (root = document) => {
    const signupHasErrors = Boolean(root.querySelector?.('[data-skoto-signup-error]'));
    let confirmedSignupAttempt = readRecentRun2Attempt();

    if (signupHasErrors) {
      try {
        window.sessionStorage.removeItem(RUN2_SIGNUP_ATTEMPT_KEY);
      } catch (_error) {
        // Leave the native Shopify error state visible when storage is unavailable.
      }
      confirmedSignupAttempt = null;
    }

    elementsWithin(root, 'form.skoto-run2-form').forEach((form) => {
      if (form.dataset.skotoSignupReady === 'true') return;
      form.dataset.skotoSignupReady = 'true';

      const size = form.querySelector('[data-skoto-size]');
      const tags = form.querySelector('[data-skoto-tags]');
      const note = form.querySelector('[data-skoto-note]');
      const consent = form.querySelector('[data-skoto-consent]');
      const source = tags?.dataset.signupSource || 'storefront_run2';

      size?.addEventListener('change', () => {
        if (!size.value) return;
        publish('run2_size_selected', { size: size.value, source });
      });

      form.addEventListener('submit', () => {
        const sizeValue = String(size?.value || '').toUpperCase();
        const campaign = attribution();
        const signupDate = new Date().toISOString();
        const consentGranted = Boolean(consent?.checked);
        const eventId = createRun2EventId();
        const qualifiedSubmission = RUN2_APPROVED_SIZES.includes(sizeValue) && consentGranted && form.checkValidity();

        if (!qualifiedSubmission) return;

        try {
          window.sessionStorage.setItem(RUN2_SIGNUP_ATTEMPT_KEY, JSON.stringify({
            event_id: eventId,
            form_id: form.id,
            source,
            hoodie_size: sizeValue,
            consent: consentGranted,
            timestamp: Date.now(),
            submitted_at: signupDate,
            page_path: window.location.pathname
          }));
        } catch (_error) {
          // The native Shopify form remains fully functional when storage is unavailable.
        }

        if (tags) {
          const attributionTags = RUN2_ATTRIBUTION_FIELDS.flatMap((key) => {
            const value = run2TagValue(campaign[key]);
            return value ? [`${RUN2_ATTRIBUTION_TAG_PREFIXES[key]}-${value}`] : [];
          });
          const signupDateTag = signupDate.slice(0, 10).replaceAll('-', '');
          const firstTouchTag = String(campaign.first_touch_at || '').slice(0, 10).replaceAll('-', '');
          const metaClickTag = campaign.meta_click === '1' ? ['meta-click-1'] : [];

          tags.value = [
            'newsletter',
            'auxesis-run-2',
            `signup-source-${source}`,
            `run2-size-${sizeValue}`,
            `signup-date-${signupDateTag}`,
            `run2-event-id-${eventId}`,
            ...(firstTouchTag ? [`attr-first-${firstTouchTag}`] : []),
            ...attributionTags,
            ...metaClickTag
          ]
            .map((value) => value.replace(/[^a-z0-9-]+/gi, '-'))
            .join(',');
        }

        if (note) {
          note.value = JSON.stringify({
            form: 'AUXESIS Run 2 access',
            hoodie_size: sizeValue,
            signup_source: source,
            signup_date: signupDate,
            consent: consentGranted,
            page: window.location.pathname,
            signup_event_id: eventId,
            ...campaign
          });
        }
      });
    });

    elementsWithin(root, '[data-skoto-signup-native-success]').forEach((success) => {
      if (success.closest('[hidden]')) return;
      if (success.dataset.skotoSuccessReady === 'true') return;
      success.dataset.skotoSuccessReady = 'true';
      success.focus({ preventScroll: true });
      success.scrollIntoView({ behavior: motionQuery.matches ? 'auto' : 'smooth', block: 'center' });
      const successForm = success.closest('form.skoto-run2-form');
      const matchesSubmission = successForm && confirmedSignupAttempt &&
        confirmedSignupAttempt.form_id === successForm.id &&
        confirmedSignupAttempt.source === success.dataset.signupSource &&
        confirmedSignupAttempt.page_path === window.location.pathname;

      if (matchesSubmission) publishRun2SignupSuccess(confirmedSignupAttempt);
    });

    elementsWithin(root, '[data-skoto-signup-error]').forEach((error) => {
      if (error.dataset.skotoErrorReady === 'true') return;
      error.dataset.skotoErrorReady = 'true';
      error.focus({ preventScroll: true });
      error.scrollIntoView({ behavior: motionQuery.matches ? 'auto' : 'smooth', block: 'center' });
    });
  };

  const initializeReveals = (root = document) => {
    const sections = elementsWithin(root, '[data-skoto-section]');

    sections.forEach((section) => {
      if (section.dataset.skotoRevealReady === 'true') return;
      section.dataset.skotoRevealReady = 'true';

      section.querySelectorAll('[data-skoto-reveal-item]').forEach((item, index) => {
        item.style.setProperty('--skoto-reveal-order', Math.min(index, 7));
      });

      if (motionQuery.matches || section.getBoundingClientRect().top < window.innerHeight * 0.92) {
        section.classList.add('is-skoto-visible');
      } else {
        revealObserver?.observe(section);
      }
    });

    document.documentElement.classList.toggle('skoto-motion-ready', !motionQuery.matches);
  };

  const initializeActiveNavigation = () => {
    document.querySelectorAll('[data-skoto-header] a[href]').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname && !url.hash) {
        link.setAttribute('aria-current', 'page');
      }
    });
  };

  const updateScrollUI = () => {
    scrollFrame = undefined;
    const header = document.querySelector('[data-skoto-header]');
    const progress = document.querySelector('.skoto-page-progress');
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const scrollProgress = Math.min(1, Math.max(0, window.scrollY / maxScroll));

    header?.classList.toggle('is-skoto-top', window.scrollY < 24);
    progress?.style.setProperty('--skoto-scroll-progress', scrollProgress.toFixed(4));

    if (!motionQuery.matches) {
      document.querySelectorAll('.skoto-campaign-hero').forEach((hero) => {
        const rect = hero.getBoundingClientRect();
        const shift = Math.max(0, Math.min(rect.height, -rect.top)) * 0.055;
        hero.style.setProperty('--skoto-hero-shift', `${shift}px`);
      });
    }
  };

  const requestScrollUpdate = () => {
    if (scrollFrame) return;
    scrollFrame = window.requestAnimationFrame(updateScrollUI);
  };

  const initializeGlobalUI = () => {
    if (!document.querySelector('.skoto-page-progress')) {
      const progress = document.createElement('div');
      progress.className = 'skoto-page-progress';
      progress.setAttribute('aria-hidden', 'true');
      document.body.prepend(progress);
    }

    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-skoto-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    heroObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.dataset.skotoInView = entry.isIntersecting ? 'true' : 'false';
        syncHero(entry.target);
      });
    }, { threshold: 0.08 });

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    desktopQuery.addEventListener?.('change', syncAllHeroes);
    motionQuery.addEventListener?.('change', () => {
      document.documentElement.classList.toggle('skoto-motion-ready', !motionQuery.matches);
      syncAllHeroes();
      initializeReveals();
      updateScrollUI();
    });
    document.addEventListener('visibilitychange', syncAllHeroes);
    updateScrollUI();
  };

  const initialize = (root = document) => {
    initializeCountdowns(root);
    initializeWallpaperGalleries(root);
    initializeRails(root);
    initializeSignupForms(root);
    initializeHeroes(root);
    initializeReveals(root);
  };

  const cleanup = (root) => {
    elementsWithin(root, '[data-skoto-countdown]').forEach((countdown) => {
      const timer = countdownTimers.get(countdown);
      if (timer) window.clearInterval(timer);
      countdownTimers.delete(countdown);
    });
    elementsWithin(root, '[data-skoto-section]').forEach((section) => revealObserver?.unobserve(section));
    elementsWithin(root, '.skoto-campaign-hero, [data-skoto-commerce-hero]').forEach((hero) => {
      heroObserver?.unobserve(hero);
      if (hero.hasAttribute('data-skoto-commerce-hero')) hero.querySelectorAll('video').forEach((video) => video.pause());
    });
    elementsWithin(root, '[data-skoto-rail]').forEach((rail) => {
      railResizeObservers.get(rail)?.disconnect();
      railResizeObservers.delete(rail);
    });
  };

  attribution();
  initializeGlobalUI();
  initializeActiveNavigation();
  initialize();

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    initializeActiveNavigation();
  });
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target));

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-skoto-event]');
    if (!target) return;

    publish(target.dataset.skotoEvent, {
      href: target.getAttribute('href') || '',
      placement: target.dataset.skotoPlacement || '',
      release_state: document.body.dataset.skotoReleaseState || 'unknown'
    });
  });
})();
